import {createApi} from '@reduxjs/toolkit/query/react'
import type {
  ICompanyBase,
  IDepartment,
  ISuccessMessage,
  TCompaniesPayload,
  TPagedCompaniesPayload,
  TUpdateStatusPayload,
} from '../../models'
import {baseQuery} from '../base-query'

export interface ICustomComplianceField {
  complianceFieldId: string
  name: string
  enabled: boolean
}

export interface IComplianceSettingsResponse {
  requiresEVerify: boolean
  requiresBackgroundCheck: boolean
  requiresDrugScreen: boolean
  /** STFFLG-4170. Optional: a BE without the field omits it; read it as `?? false`. */
  requiresAdmin?: boolean
  customComplianceFields: ICustomComplianceField[]
}

export interface IAutomationSettingsResponse {
  autoPledge: boolean
  autoConfigure: boolean
  autoApproveCandidates: boolean
  minOrderLeadTimeMinutes?: number
}

export const companyApi = createApi({
  reducerPath: 'companyApi',
  baseQuery: baseQuery,
  tagTypes: ['Company'],
  endpoints: (builder) => ({
    getCompanies: builder.query<TCompaniesPayload, TPagedCompaniesPayload>({
      providesTags: ['Company'],
      query: ({pageNumber, pageSize}) => ({
        url: `/api/Company/GetCompanies`,
        method: 'GET',
        params: {pageNumber, pageSize},
      }),
    }),

    getCompanyNameById: builder.query<string, TPagedCompaniesPayload>({
      providesTags: ['Company'],
      query: ({id}) => ({
        url: `/api/Company/GetCompanyNameById/${id}`,
        method: 'GET',
        responseHandler: 'text',
      }),
    }),

    getCompanyNames: builder.query<string[], string>({
      providesTags: ['Company'],
      query: (agencyId) => ({
        url: `/api/Company/getCompanyNames/Names`,
        method: 'GET',
        params: {agencyId},
      }),
    }),

    getAllCompanyNames: builder.query<
      string[],
      {pageNumber: number; pageSize: number; agencyId?: string}
    >({
      providesTags: ['Company'],
      query: ({pageNumber, pageSize, agencyId}) => ({
        url: `/api/Company/getAllCompanyNames`,
        method: 'GET',
        params: {pageNumber, pageSize, agencyId},
      }),
    }),

    getAllCompanyNamesAndIds: builder.query<
      {id: string; name: string}[],
      {pageNumber: number; pageSize: number; agencyId?: string}
    >({
      providesTags: ['Company'],
      query: ({pageNumber, pageSize, agencyId}) => ({
        url: `/api/company/getAllCompanyNamesAndIds`,
        method: 'GET',
        params: {pageNumber, pageSize, agencyId},
      }),
    }),

    getOneCompany: builder.query<ICompanyBase, string>({
      providesTags: ['Company'],
      query: (companyId) => ({
        url: `/api/Company/GetCompany/${companyId}`,
        method: 'GET',
      }),
      transformResponse: (company: ICompanyBase) => {
        return company
      },
    }),

    findCompany: builder.query<boolean, string>({
      providesTags: ['Company'],
      query: (name) => ({
        url: `/api/Company/FindCompanyName/Find/${name}`,
        method: 'GET',
      }),
    }),

    findCompaniesAssignedToMsp: builder.query<boolean, string>({
      providesTags: ['Company'],
      query: (mspId) => ({
        url: `/api/Company/GetCompanyIdsWithAssignedMspId/${mspId}`,
        method: 'GET',
      }),
    }),

    getDepartments: builder.query<IDepartment[], string>({
      providesTags: ['Company'],
      query: (companyId) => ({
        url: `/api/Company/${companyId}`,
        method: 'GET',
      }),
      transformResponse: ({departments}) => {
        return departments
      },
    }),

    createCompany: builder.mutation<ISuccessMessage, Partial<ICompanyBase>>({
      invalidatesTags: ['Company'],
      query: (body) => ({
        url: `/api/Company/OnboardCompany`,
        method: 'POST',
        body: body,
      }),
    }),

    updateCompanyStatus: builder.mutation<ISuccessMessage, TUpdateStatusPayload>({
      invalidatesTags: ['Company'],
      query: ({id, status}) => ({
        url: `/api/Company/UpdateCompanyStatus/${id}/Status/${status}`,
        method: 'PUT',
      }),
    }),

    updateCompany: builder.mutation<ISuccessMessage, ICompanyBase>({
      invalidatesTags: ['Company'],
      query: (body) => ({
        url: `/api/Company/UpdateCompany`,
        method: 'PUT',
        body: body,
      }),
    }),

    deleteCompany: builder.mutation<ISuccessMessage, string>({
      invalidatesTags: ['Company'],
      query: (id) => ({
        url: `/Company/DeleteCompany`,
        method: 'DELETE',
        params: {id},
      }),
    }),

    getComplianceSettings: builder.query<IComplianceSettingsResponse, string>({
      providesTags: (_result, _error, id) => [{type: 'Company', id: `compliance-${id}`}],
      query: (companyId) => ({
        url: `/api/company/getComplianceSettings/${companyId}`,
        method: 'GET',
      }),
    }),

    updateComplianceSettings: builder.mutation<
      IComplianceSettingsResponse,
      {
        companyId: string
        settings: {
          requiresEVerify?: boolean
          requiresBackgroundCheck?: boolean
          requiresDrugScreen?: boolean
          requiresAdmin?: boolean
          customComplianceFields?: Array<{complianceFieldId: string; enabled: boolean}>
        }
      }
    >({
      invalidatesTags: (_result, _error, {companyId}) => [
        {type: 'Company', id: `compliance-${companyId}`},
      ],
      query: ({companyId, settings}) => ({
        url: `/api/company/updateComplianceSettings/${companyId}`,
        method: 'PUT',
        body: settings,
      }),
    }),

    addCustomComplianceField: builder.mutation<
      IComplianceSettingsResponse,
      {companyId: string; complianceFieldId: string}
    >({
      invalidatesTags: (_result, _error, {companyId}) => [
        {type: 'Company', id: `compliance-${companyId}`},
      ],
      query: ({companyId, complianceFieldId}) => ({
        url: `/api/company/${companyId}/customComplianceField`,
        method: 'POST',
        body: {complianceFieldId},
      }),
    }),

    removeCustomComplianceField: builder.mutation<
      IComplianceSettingsResponse,
      {companyId: string; complianceFieldId: string}
    >({
      invalidatesTags: (_result, _error, {companyId}) => [
        {type: 'Company', id: `compliance-${companyId}`},
      ],
      query: ({companyId, complianceFieldId}) => ({
        url: `/api/company/${companyId}/customComplianceField/${complianceFieldId}`,
        method: 'DELETE',
      }),
    }),

    getAutomationSettings: builder.query<IAutomationSettingsResponse, string>({
      providesTags: (_result, _error, id) => [{type: 'Company', id: `automation-${id}`}],
      query: (companyId) => ({
        url: `/api/company/getAutomationSettings/${companyId}`,
        method: 'GET',
      }),
    }),

    updateAutomationSettings: builder.mutation<
      IAutomationSettingsResponse,
      {
        companyId: string
        settings: Required<IAutomationSettingsResponse>
      }
    >({
      invalidatesTags: (_result, _error, {companyId}) => [
        {type: 'Company', id: `automation-${companyId}`},
      ],
      query: ({companyId, settings}) => ({
        url: `/api/company/updateAutomationSettings/${companyId}`,
        method: 'PUT',
        body: settings,
      }),
    }),
  }),
})

export const {
  useGetCompaniesQuery,
  useGetAllCompanyNamesQuery,
  useGetAllCompanyNamesAndIdsQuery,
  useGetCompanyNameByIdQuery,
  useLazyGetCompanyNamesQuery,
  useCreateCompanyMutation,
  useUpdateCompanyStatusMutation,
  useDeleteCompanyMutation,
  useGetOneCompanyQuery,
  useUpdateCompanyMutation,
  useLazyGetOneCompanyQuery,
  useGetDepartmentsQuery,
  useLazyGetDepartmentsQuery,
  usePrefetch,
  useLazyFindCompanyQuery,
  useFindCompaniesAssignedToMspQuery,
  useGetComplianceSettingsQuery,
  useUpdateComplianceSettingsMutation,
  useAddCustomComplianceFieldMutation,
  useRemoveCustomComplianceFieldMutation,
  useGetAutomationSettingsQuery,
  useUpdateAutomationSettingsMutation,
} = companyApi
